import {MouseEvent, KeyboardEvent, useState, useEffect, useRef} from 'react';
import {Box, Typography, Grid} from '@mui/material';
import {t} from 'i18next';
import ArrRadio from '../../../../../common/arr-radio/ArrRadio';
import './IssueCodingTypeHeader.scss';
import ReplaceManualContentModal from './replace-manual-content-modal/ReplaceManualContentModal';
import SwitchToManualModal from './switch-to-manual-modal/SwitchToManualModal';
import {RootState} from '../../../../../../redux/store';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {
  setDefineCriteria,
  setFormData,
} from '../../../../../../redux/slice/aiReviewSlice';
import {Trans} from 'react-i18next';
import {ISelectItem} from '../../../../../../shared/types';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ReactComponent as RowChecked} from '../../../../../../assets/images/row-checked.svg';
import {AI_REVIEW_JOB_TYPE} from '../../../constant/AiReviewConstants';
import {formatUtcTimestamp} from '../../../protocolIntelligence/protocolIntelligenceContent/generateArtifacts/GenerateArtifacts';

/** Constants for criteria values (no magic strings) */
export const CRITERIA = {
  MANUAL: 'manual',
  SAVED: 'saved',
} as const;

type CriteriaValue = (typeof CRITERIA)[keyof typeof CRITERIA];

type CriteriaOption = {
  value: CriteriaValue;
  label: string;
};

type CriteriaRadioConfig = {
  id: CriteriaValue;
  options: CriteriaOption[]; // usually single-option-per-card in your design
};

interface IssueCodingTypeHeaderProps {
  isProtocolDisabled: boolean;
  handleUseSavedProtocol: () => void;
  clearAllContents: () => void;
  protocolList: ISelectItem[];
}

const IssueCodingTypeHeader = ({
  isProtocolDisabled,
  handleUseSavedProtocol,
  clearAllContents,
  protocolList,
}: IssueCodingTypeHeaderProps) => {
  const [showReplaceManualModal, setShowReplaceManualModal] =
    useState<boolean>(false);
  const [showSwitchToManualModal, setShowSwitchToManualModal] =
    useState<boolean>(false);
  const [selectedProtocol, setSelectedProtocol] = useState<ISelectItem | null>(
    null
  );
  const [pendingProtocol, setPendingProtocol] = useState<ISelectItem | null>(
    null
  );
  const store = useStore();

  const defineCriteriaState = useSelector(
    (state: RootState) => state.aiReview.defineCriteria
  );

  const formData = useSelector((state: RootState) => state.aiReview.formData);
  const editMode = useSelector((state: RootState) => state.aiReview.editMode);

  const jobType = useSelector((state: RootState) => state.aiReview.jobType);

  const currentEditMode = (store.getState() as RootState).aiReview.editMode;
  const cloneJobName = useSelector(
    (state: RootState) => state.aiReview.cloneJobName
  );
  const isEditOrCloneFlow = !!formData.jobId || !!cloneJobName;
  const dispatch = useDispatch();
  const defineCriteria = isEditOrCloneFlow
    ? formData.isManual
      ? CRITERIA.MANUAL
      : CRITERIA.SAVED
    : defineCriteriaState;

  const hasSetInitialCriteriaRef = useRef(false);

  const disableSavedProtocol = protocolList.length === 0;

  const protocolIdToValidate = formData.isProtocolModified
    ? formData.parentProtocolID
    : formData.reviewProtocolID;
  const shouldUseManual =
    protocolList.length === 0 ||
    formData.isManual ||
    isProtocolDisabled ||
    (protocolIdToValidate &&
      !protocolList.some((p) => Number(p.id) === Number(protocolIdToValidate)));

  useEffect(() => {
    if (hasSetInitialCriteriaRef.current) return;

    hasSetInitialCriteriaRef.current = true;

    if (isEditOrCloneFlow) {
      // Inactive protocol -> force Manual
      if (shouldUseManual) {
        dispatch(setFormData({isManual: true}));
        dispatch(setDefineCriteria(CRITERIA.MANUAL));
        return;
      }

      // Active protocol -> respect saved state
      dispatch(
        setDefineCriteria(formData.isManual ? CRITERIA.MANUAL : CRITERIA.SAVED)
      );

      if (
        !formData.isManual &&
        !editMode.caseContext &&
        !editMode.relevanceCriteria &&
        !editMode.issueCode
      ) {
        handleUseSavedProtocol();
      }

      return;
    }

    // Fresh Create flow
    if (shouldUseManual) {
      dispatch(setDefineCriteria(CRITERIA.MANUAL));
      dispatch(setFormData({isManual: true}));
      return;
    }

    dispatch(setDefineCriteria(CRITERIA.SAVED));
    dispatch(setFormData({isManual: false}));
    if (
      !editMode.caseContext &&
      !editMode.relevanceCriteria &&
      !editMode.issueCode
    ) {
      handleUseSavedProtocol();
    }
  }, [isProtocolDisabled, isEditOrCloneFlow, formData.isManual]);
  // Auto-select first item from API as default, this should be changed
  // After — syncs both local state and Redux on initial load

  useEffect(() => {
    if (!formData.reviewProtocolID && !formData.parentProtocolID) {
      setSelectedProtocol(null);
    }
  }, [formData.reviewProtocolID, formData.parentProtocolID]);
  // Auto-select protocol shown in the dropdown:
  // - modified flow (BE created a new reviewProtocolID from parentProtocolID):
  //     show the ORIGINAL parent protocol's name, content stays as reviewProtocolID's
  // - normal saved flow: show whatever reviewProtocolID currently is
  // - fresh flow (nothing set yet): default to first item, same as before
  useEffect(() => {
    if (protocolList.length === 0) return;

    const idToMatch = formData.isProtocolModified
      ? formData.parentProtocolID
      : formData.reviewProtocolID;

    if (idToMatch) {
      const match = protocolList.find(
        (p) => Number(p.id) === Number(idToMatch)
      );
      if (match) {
        setSelectedProtocol(match);
        return; // don't touch reviewProtocolID — already correctly set from load
      }
    }

    if (isEditOrCloneFlow && idToMatch) {
      // Existing job but its protocol is no longer active.
      // Show the first active protocol in the dropdown only.
      // Preserve reviewProtocolID so the job content stays unchanged.
      setSelectedProtocol(protocolList[0] ?? null);
      return;
    }

    // No protocol context at all — fresh flow, default to first item
    const first = protocolList[0];
    setSelectedProtocol(first);
    if (
      first?.id &&
      !formData.isManual &&
      Number(formData.reviewProtocolID) !== Number(first.id)
    ) {
      dispatch(setFormData({reviewProtocolID: Number(first.id)}));
    }
  }, [
    protocolList,
    formData.isProtocolModified,
    formData.parentProtocolID,
    formData.reviewProtocolID,
    formData.isManual,
  ]);

  const criteriaRadioOptions: CriteriaRadioConfig[] = [
    {
      id: CRITERIA.SAVED,
      options: [
        {
          value: CRITERIA.SAVED,
          label: t('ai.review.newJobDetail.defineCriteriaSavedLabel'),
        },
      ],
    },
    {
      id: CRITERIA.MANUAL,

      options: [
        {
          value: CRITERIA.MANUAL,
          label: t('ai.review.newJobDetail.defineCriteriaManuallyLabel'),
        },
      ],
    },
  ];

  const cards = criteriaRadioOptions;

  const handleReplaceManualModalClose = () => {
    setPendingProtocol(null);
    setShowReplaceManualModal(false);
  };
  const handleSwitchToManualClose = () => {
    setShowSwitchToManualModal(false);
  };

  const selectValue = (next: string) => {
    if (defineCriteria !== next) dispatch(setDefineCriteria(next));
  };

  const handleRadioChange = (val: string) => {
    if (val === CRITERIA.SAVED && defineCriteria === CRITERIA.MANUAL) {
      setShowReplaceManualModal(true);
      return;
    }
    if (val === CRITERIA.MANUAL && defineCriteria === CRITERIA.SAVED) {
      setShowSwitchToManualModal(true);
      return;
    }
    dispatch(setDefineCriteria(val));
  };

  // Clicking anywhere on the card selects the radio
  const handleCardClick =
    (value: CriteriaValue) => (_e: MouseEvent<HTMLDivElement>) => {
      if (value === CRITERIA.SAVED && defineCriteria === CRITERIA.MANUAL) {
        setShowReplaceManualModal(true);
      }
      if (value === CRITERIA.MANUAL && defineCriteria === CRITERIA.SAVED) {
        setShowSwitchToManualModal(true);
      }
      selectValue(value);
    };

  // Keyboard: Space/Enter selects the card (WAI-ARIA radio pattern)
  const handleCardKeyDown =
    (value: CriteriaValue) => (e: KeyboardEvent<HTMLDivElement>) => {
      const key = e.key.toLowerCase();
      if (key === ' ' || key === 'enter') {
        e.preventDefault();
        if (value === CRITERIA.SAVED && defineCriteria === CRITERIA.MANUAL) {
          setShowReplaceManualModal(true);
        }
        if (value === CRITERIA.MANUAL && defineCriteria === CRITERIA.SAVED) {
          setShowSwitchToManualModal(true);
        }
        selectValue(value);
      }
    };

  const applyProtocol = (value: ISelectItem | null) => {
    setSelectedProtocol(value);

    if (value?.id) {
      dispatch(
        setFormData({
          reviewProtocolID: Number(value.id),
          parentProtocolID: null,
          isProtocolModified: false,
          isManual: false,
        })
      );
    }
  };

  return (
    <div
      className="issue-coding-type-header"
      data-testid={`issue-coding-type-header-issue-coding-header-title-wrapper`}
    >
      <Box>
        <Typography
          className="text-headings typo-text subhead3_size"
          data-testid="issue-coding-header-title"
          tabIndex={0}
        >
          {t('ai.review.newJobDetail.issueCodingHeaderTitle')}
        </Typography>
        <Typography
          className="text-body-subtle typo-text body2_size"
          data-testid="issue-coding-header-title"
          tabIndex={0}
        >
          <Trans i18nKey="ai.review.newJobDetail.issueCodingHeaderDesc" />
        </Typography>
      </Box>
      <Box className="define-criteria">
        <Grid
          container
          spacing={3}
          role="radiogroup"
          aria-label={t('ai.review.newJobDetail.defineCriteriaAriaLabel')}
          data-testid="define-criteria-radiogroup"
        >
          {cards.map(({id, options}) => {
            const selected = defineCriteria === id;
            return (
              <Grid
                key={id}
                size={{xs: 12, sm: id === CRITERIA.SAVED ? 8 : 4}}
                className="define-criteria-box"
                data-testid={`criteria-grid-item-${id}`}
              >
                <Box
                  className={
                    id === CRITERIA.SAVED
                      ? 'criteria-card-with-dropdown'
                      : 'criteria-card-manual-wrapper'
                  }
                >
                  {id === CRITERIA.MANUAL && (
                    <Box
                      className="criteria-divider"
                      data-testid="criteria-divider"
                    />
                  )}
                  <Box
                    role="radio"
                    aria-checked={selected}
                    tabIndex={!disableSavedProtocol || selected ? 0 : -1}
                    onClick={
                      !disableSavedProtocol ? handleCardClick(id) : undefined
                    }
                    className={`criteria-card ${selected ? 'is-selected' : ''}`}
                    onKeyDown={handleCardKeyDown(id)}
                    data-testid={`criteria-card-${id}`}
                  >
                    <span
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`issue-coding-type-header-span-${id}`}
                    >
                      <ArrRadio
                        name="define-criteria"
                        className="define-criteria-radio"
                        value={defineCriteria}
                        isAriaDisabled={
                          disableSavedProtocol && id === CRITERIA.SAVED
                        }
                        disabled={disableSavedProtocol && id === CRITERIA.SAVED}
                        onChange={(val: string) => handleRadioChange(val)}
                        data={options}
                        size="small"
                        dataTestId={`criteria-card-${id}-radio`}
                        tabIndex={-1}
                        ariaLabel={
                          id === CRITERIA.MANUAL
                            ? t(
                                'ai.review.newJobDetail.defineCriteriaManuallyLabel'
                              )
                            : t(
                                'ai.review.newJobDetail.defineCriteriaSavedLabel'
                              )
                        }
                      />
                    </span>
                  </Box>

                  {id === CRITERIA.SAVED && (
                    <Box
                      className="protocol-select"
                      tabIndex={-1}
                      data-testid="protocol-dropdown"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ArrSingleSelectDropdown
                        menuPropClassName="protocol-dropdown-menu"
                        dropdownLabel={t(
                          'ai.review.newJobDetail.selectProtocol'
                        )}
                        dropdownItems={protocolList}
                        renderPrefix={(item) => (
                          <Box className="protocol-dropdown-item">
                            <Box className="selected-item">
                              <Typography className="protocol-dropdown-item__name text-headings typo-text subhead5_size">
                                {item.name as string}
                              </Typography>
                              <Box>
                                {selectedProtocol?.id === item.id && (
                                  <RowChecked
                                    className="protocol-dropdown-item__check"
                                    height={16}
                                    width={16}
                                  />
                                )}
                              </Box>
                            </Box>
                            <Typography className="protocol-dropdown-item__sub text-body-strong typo-text body4_size">
                              {item.uploaded
                                ? t('ai.review.protocolIntelligence.uploaded')
                                : t('ai.investigate.listItem.created')}
                              <br />
                              {`${formatUtcTimestamp(item.createdAt)} by ${item.createdBy}`}
                            </Typography>
                          </Box>
                        )}
                        selectedValues={pendingProtocol ?? selectedProtocol}
                        searchable
                        dropdownItemDisplay="name"
                        dropdownItemKey="id"
                        disabled={defineCriteria !== CRITERIA.SAVED}
                        onSelectionChange={(value) => {
                          const hasEditChanges =
                            jobType === AI_REVIEW_JOB_TYPE.issueCoding
                              ? currentEditMode.issueCode ||
                                currentEditMode.caseContext
                              : currentEditMode.relevanceCriteria ||
                                currentEditMode.caseContext;

                          if (hasEditChanges) {
                            setPendingProtocol(value);
                            setShowReplaceManualModal(true);
                            return;
                          }

                          applyProtocol(value);
                        }}
                        dataTestId="protocol-select-dropdown"
                        sizeVariant="medium"
                        tabIndex={-1}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <ReplaceManualContentModal
        open={showReplaceManualModal}
        onClose={handleReplaceManualModalClose}
        setShowReplaceManualModal={setShowReplaceManualModal}
        onConfirm={() => {
          const protocolToApply = pendingProtocol ?? protocolList[0];

          if (!protocolToApply) return;

          applyProtocol(protocolToApply);
          setPendingProtocol(null);
        }}
      />
      <SwitchToManualModal
        open={showSwitchToManualModal}
        onClose={handleSwitchToManualClose}
        setShowSwitchToManualModal={setShowSwitchToManualModal}
        clearAllContents={clearAllContents}
      />
    </div>
  );
};

export default IssueCodingTypeHeader;
